import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, Button, TextField } from '@mui/material';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import styled from '@emotion/styled';

const ModalOverlay = styled(motion.div)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
});

const ModalContent = styled(motion.div)({
  background: 'white',
  borderRadius: '24px',
  maxWidth: '500px',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
});

const DeleteModal = ({ isOpen, onClose, onDelete, brandName }) => {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmValid = confirmText === brandName;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <Box sx={{ p: 4 }}>
              <Box sx={{ position: 'absolute', right: 16, top: 16 }}>
                <Button
                  onClick={onClose}
                  sx={{ minWidth: 'auto', p: 1, color: 'gray' }}
                >
                  <X size={20} />
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3
                    }}
                  >
                    <AlertTriangle size={40} color="#ef4444" />
                  </Box>
                </motion.div>

                <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 600, mb: 1 }}>
                  Delete Brand
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  This action cannot be undone. This will permanently delete the
                  <br />
                  <strong>{brandName}</strong> brand and remove all associated data.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                    Please type <strong>{brandName}</strong> to confirm
                  </Typography>
                  <TextField
                    fullWidth
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={`Type "${brandName}" to confirm`}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      }
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      borderRadius: '12px',
                      px: 4,
                      borderColor: '#e5e7eb',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: '#d1d5db',
                        bgcolor: '#f9fafb'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onDelete}
                    disabled={!isConfirmValid}
                    startIcon={<Trash2 size={16} />}
                    sx={{
                      borderRadius: '12px',
                      px: 4,
                      bgcolor: '#ef4444',
                      '&:hover': { bgcolor: '#dc2626' },
                      '&.Mui-disabled': {
                        bgcolor: '#fca5a5'
                      }
                    }}
                  >
                    Delete Brand
                  </Button>
                </Box>
              </Box>
            </Box>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default DeleteModal;
